import dayjs from "dayjs";
import Decimal from "decimal.js";
import $ from "jquery";
import MP4Box, { BoxParser } from "mp4box";
import { Button, Selectbox } from "./Form";
import { outputFilePrefix } from "./OutputName";
import { createViewerLink } from "./OutputViewer";
import type { RenderCoordinator } from "./RenderCoordinator";

export interface VideoRequest {
    ftStart?: number;
    ftEnd?: number;
    ftStep?: number;
    fps?: number;
    width?: number;
    height?: number;
    quality?: "low" | "std" | "high" | "max";
    bitrateKbps?: number;
    audio?: boolean;
    preferSoftware?: boolean;
    onProgress?: (done: number, total: number) => void;
    signal?: AbortSignal;
}

export interface VideoMeta {
    frames: number;
    keyFrames: number;
    width: number;
    height: number;
    fps: number;
    durationSec: number;
    ftStart: string;
    ftEnd: string;
    ftStep: string;
    codec: string;
    bitrateMode: "quantizer" | "variable";
    bitrate: number | null;
    elapsedMs: number;
}

export interface VideoResult {
    blob: Blob;
    meta: VideoMeta;
}

// App 側の描画機能への窓口 (App が実装を渡す)
export interface VideoHost {
    renderFrame(ft: Decimal): Promise<void>;
    prepareFrames?(fts: Decimal[]): Promise<void>;
    getForecastTime(): Decimal;
    getRange(): { interval: Decimal; minimum: Decimal; maximum: Decimal };
    waitIdle(): Promise<unknown>;
    getQuery(): string; // 現在の表示状態の URL クエリ (CF サーバー生成用)
    isVideoAvailable?(): boolean;
}

export interface VideoFrameSource {
    getSelectedSize(): [number, number];
    toCanvas(width: number, height: number): Promise<HTMLCanvasElement>;
    beginVideoCapture(): void;
    endVideoCapture(): void;
}

const CODEC = "avc1.640034"; // H.264 High L5.2 (4K 60fps まで)
const MAX_FRAMES = 1500;
const MAX_QUEUE = 4; // VideoEncoder への滞留上限 (超えたら dequeue を待つ)
const QUANTIZER = 18; // CQP 時の品質 (小さいほど高画質。18 ≒ 視覚的にほぼ無劣化)
// bits/pixel/frame。旧サーバー動画 (libx264 -crf 18) は実測 ~0.4bpp 相当で、
// 0.2bpp では見劣りした (2026-07-13 比較)。「最高」は CQP18 (crf18 と同思想)、
// CQP 非対応ブラウザは 0.45bpp VBR で旧と同水準にする。
const BPP_TABLE: Record<string, number> = {
    low: 0.1,
    std: 0.2,
    high: 0.3,
    max: 0.45, // CQP 非対応環境のフォールバック
};

// CF Worker (Browser Run) のサーバー生成 API。draw3 の Origin は CORS 許可済み
const CF_VIDEO_ENDPOINT = "https://gpv-video.wmap.workers.dev";
// Cloudflare障害時の別サーバー。描画元はCloud Run側でdraw3に固定し、
// 完成MP4を必ずffmpeg/libx264へ通す。受付はGPVページのOrigin+レート制限のみ
const GCP_VIDEO_ENDPOINT =
    "https://gpv-video-api-385336581517.asia-northeast1.run.app";
// test alias から呼ぶ生成は worker 側も test の Pages を開く (Origin 判定と二重化)
const CF_TEST_HOST = "test.gpv-cf.pages.dev";

interface ServerVideoRoute {
    name: string;
    reencode: boolean;
    useCfTestTarget?: boolean;
}

export class VideoRecorder {
    private videoMode!: Selectbox;
    private fps!: Selectbox;
    private quality!: Selectbox;
    private link!: Element;
    private abortController: AbortController | null = null;
    private serverAbortController: AbortController | null = null;
    private gcpJob: { id: string; accessToken: string } | null = null;
    private objectUrl: string | null = null;
    private available = true;
    // bindUi=false は record()/abort() を直接呼ぶ利用 (NOWC250-2.0 等)。
    // GPV 標準 UI (#to_avi/#sb_vmode/#sb_fps/#sb_vq/#toPNGs_stop/#link_png) を要求しない。
    constructor(
        private readonly host: VideoHost,
        private readonly downloader: VideoFrameSource,
        private readonly coordinator: RenderCoordinator,
        bindUi = true,
    ) {
        if (bindUi) {
            this.videoMode = new Selectbox("#sb_vmode");
            this.fps = new Selectbox("#sb_fps");
            this.quality = new Selectbox("#sb_vq");
            this.link = document.querySelector("#link_png")!;
            new Button("#to_avi").onClick(() => {
                // 出力タブの動画モード: 自動判定 / ブラウザ /
                // サーバー軽・重 (CF) / 別サーバー (Cloud Run+必須ffmpeg)
                const mode = this.resolveVideoMode();
                if (mode === "browser") {
                    this.downloadVideo();
                } else if (mode === "gcp") {
                    this.downloadVideoViaGcp();
                } else {
                    this.downloadVideoViaServer({
                        name: mode === "cf2" ? "サーバー重" : "サーバー軽",
                        reencode: mode === "cf2",
                        useCfTestTarget: true,
                    });
                }
            });
            new Button("#toPNGs_stop").onClick(() => this.abort());
            this.updateAvailability();
        }
        window.addEventListener("beforeunload", () => this.revokeObjectUrl());
    }
    get running() {
        return (
            this.abortController !== null || this.serverAbortController !== null
        );
    }
    onShadeVariableChanged() {
        this.updateAvailability();
    }
    abort() {
        this.abortController?.abort(new Error("動画作成を中止しました。"));
        this.serverAbortController?.abort();
        if (this.gcpJob) {
            const { id, accessToken } = this.gcpJob;
            fetch(`${GCP_VIDEO_ENDPOINT}/jobs/${id}`, {
                method: "DELETE",
                headers: { "X-Video-Token": accessToken },
                keepalive: true,
            }).catch(() => {});
        }
    }
    async record(request: VideoRequest): Promise<VideoResult> {
        if (!this.isAvailable()) {
            throw new Error("発雷確率は動画を作成できません。");
        }
        if (this.running) {
            throw new Error("動画作成は同時に1本までです。");
        }
        this.abortController = new AbortController();
        await this.host.waitIdle();
        const task = this.run(request, this.abortController.signal).finally(
            () => {
                this.abortController = null;
            },
        );
        // 進捗を renderReady 系 API へ反映する (待つのは task 本体)。
        // 失敗・中止は task の reject で呼び出し元へ伝わるため、
        // coordinator にエラーを残さない (残すと以後の waitForRender が失敗し続ける)。
        this.coordinator.track("video", task).catch(() => {
            this.coordinator.clearError("video");
        });
        return task;
    }
    // UI の共通入力 (従来 toPNGs と同じ「現在FTからN時間先まで」「FT/s」)
    private uiRequest() {
        const hoursInput = Number($("#input_topngs_hour").val());
        const hours =
            Number.isFinite(hoursInput) && hoursInput > 0 ? hoursInput : 0;
        const range = this.host.getRange();
        const start = this.host.getForecastTime();
        const end = Decimal.min(start.add(hours), range.maximum);
        const step = Decimal.max(range.interval.mul(0.1), new Decimal(0.1));
        return {
            ftStart: start.toNumber(),
            ftEnd: end.toNumber(),
            ftStep: step.toNumber(),
            fps: Number(this.fps.value),
            quality: this.quality.value as VideoRequest["quality"],
        };
    }
    // 自動判定: AUTO_BROWSER_MAX_FRAMES 以下はブラウザ生成、超はサーバー軽 (CF)。
    // 181フレームは 0.1h 刻みで18時間分に相当する
    private static readonly AUTO_BROWSER_MAX_FRAMES = 181;
    private resolveVideoMode() {
        const value = this.videoMode.value;
        if (value !== "auto") {
            return value;
        }
        const { ftStart, ftEnd, ftStep } = this.uiRequest();
        const frames = Math.floor((ftEnd - ftStart) / ftStep + 1e-9) + 1;
        return frames <= VideoRecorder.AUTO_BROWSER_MAX_FRAMES
            ? "browser"
            : "cf";
    }
    private setUiBusy(busy: boolean) {
        $("#to_avi").prop("disabled", busy || !this.available);
        if (busy) {
            $("#download_image").attr("disabled", "");
            $("#toPNGs_stop").show();
        } else {
            $("#download_image").removeAttr("disabled");
            $("#toPNGs_stop").hide();
        }
    }
    // ブラウザ内生成中は表示中SVGをそのままフレームへ使うため、FT・要素・
    // 右UIパネル等を操作すると変なフレームが混ざる。ヘッダーと右パネルを
    // inert+グレーアウトで操作不能にする (下部の作成中止・進捗は残す)
    private setCaptureLock(locked: boolean) {
        for (const selector of ["#header", "#panel"]) {
            const element = document.querySelector(selector);
            element?.toggleAttribute("inert", locked);
            element?.classList.toggle("capture_lock", locked);
        }
    }
    private isAvailable() {
        return this.host.isVideoAvailable?.() ?? true;
    }
    private updateAvailability() {
        this.available = this.isAvailable();
        $("#to_avi")
            .attr("data-video-unavailable", String(!this.available))
            .prop("disabled", !this.available);
        $("#sb_fps, #sb_vq, #sb_vmode").prop("disabled", !this.available);
    }
    private finishDownload(blob: Blob, label: string) {
        this.revokeObjectUrl();
        this.objectUrl = URL.createObjectURL(blob);
        const filename = `${outputFilePrefix()}_${dayjs().format("YYYYMMDD_HHmmss")}.mp4`;
        this.link.textContent = "";
        // 動画は生成に時間がかかりユーザー操作の有効期間 (約5秒) を確実に
        // 過ぎるため、完成時の自動オープンや自動DLはせず、完成リンクの
        // クリックでビューアタブを開いてもらう
        this.link.appendChild(
            createViewerLink({
                url: this.objectUrl,
                filename,
                kind: "video",
                label,
            }),
        );
    }
    private async downloadVideo() {
        this.setUiBusy(true);
        this.setCaptureLock(true);
        this.link.textContent = "動画作成中...";
        try {
            const { blob, meta } = await this.record({
                ...this.uiRequest(),
                onProgress: (done, total) => {
                    this.link.textContent = `動画作成中: ${done} / ${total}`;
                },
            });
            this.finishDownload(
                blob,
                `動画完成 (${meta.frames}コマ ${meta.durationSec.toFixed(1)}秒)`,
            );
        } catch (error) {
            const message =
                error instanceof Error ? error.message : String(error);
            this.link.textContent = `動画作成失敗: ${message}`;
            console.error(error);
        } finally {
            this.setCaptureLock(false);
            this.setUiBusy(false);
        }
    }
    // CF WorkerまたはCloud Runに生成させる。どちらも同じ進捗ストリーム契約を使う。
    // Cloud Runの「別サーバー」はサーバー側でもffmpeg再エンコードを強制する。
    private async downloadVideoViaServer(route: ServerVideoRoute) {
        if (this.running) {
            return;
        }
        const [width, height] = this.downloader.getSelectedSize();
        const request = this.uiRequest();
        this.serverAbortController = new AbortController();
        this.setUiBusy(true);
        const startedAt = Date.now();
        let progressText = "";
        const timer = window.setInterval(() => {
            const sec = Math.round((Date.now() - startedAt) / 1000);
            this.link.textContent = `${route.name}で動画生成中${progressText}... ${sec}s`;
        }, 1000);
        this.link.textContent = `${route.name}で動画生成中...`;
        try {
            const response = await fetch(`${CF_VIDEO_ENDPOINT}/video`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    query: Object.fromEntries(
                        new URLSearchParams(this.host.getQuery()),
                    ),
                    video: { ...request, width, height },
                    progressStream: true,
                    reencode: route.reencode,
                    ...(route.useCfTestTarget &&
                    location.hostname === CF_TEST_HOST
                        ? { target: "test" }
                        : {}),
                }),
                signal: this.serverAbortController.signal,
            });
            if (!response.ok) {
                let detail = `HTTP ${response.status}`;
                try {
                    detail = JSON.parse(await response.text()).error ?? detail;
                } catch {
                    // JSON でなければ HTTP ステータスのまま
                }
                throw new Error(detail);
            }
            let blob: Blob;
            let meta: { frames?: number; workerElapsedMs?: number };
            if (
                response.headers.get("Content-Type")?.startsWith("video/") ||
                !response.body
            ) {
                // 旧 worker (非ストリーミング) との互換
                blob = await response.blob();
                meta = JSON.parse(response.headers.get("X-Video-Meta") ?? "{}");
            } else {
                const result = await this.readProgressStream(
                    response.body,
                    (done, total, stage) => {
                        if (stage === "reencode") {
                            progressText = " 再エンコード中";
                        } else {
                            progressText =
                                total > 0
                                    ? ` ${Math.floor((done / total) * 100)}% (${done}/${total}コマ)`
                                    : "";
                        }
                    },
                );
                blob = result.blob;
                meta = result.meta;
            }
            const info = meta.frames
                ? ` (${meta.frames}コマ ${Math.round((meta.workerElapsedMs ?? 0) / 1000)}s)`
                : "";
            this.finishDownload(blob, `動画完成(${route.name})${info}`);
        } catch (error) {
            const message =
                error instanceof Error && error.name === "AbortError"
                    ? "中止しました。"
                    : error instanceof Error
                      ? error.message
                      : String(error);
            this.link.textContent = `${route.name}動画生成失敗: ${message}`;
            console.error(error);
        } finally {
            window.clearInterval(timer);
            this.serverAbortController = null;
            this.setUiBusy(false);
        }
    }
    // Cloudflare障害時用のGoogle Cloud経路。受付だけ同期し、Cloud Tasks →
    // Cloud Run Jobの完了をjob IDでpollする。ブラウザ切断後も状態を見失わない。
    private async downloadVideoViaGcp() {
        if (this.running) {
            return;
        }
        const [width, height] = this.downloader.getSelectedSize();
        const requestBody = JSON.stringify({
            query: Object.fromEntries(
                new URLSearchParams(this.host.getQuery()),
            ),
            video: { ...this.uiRequest(), width, height },
        });
        this.serverAbortController = new AbortController();
        this.setUiBusy(true);
        const startedAt = Date.now();
        let statusText = "受付中";
        const timer = window.setInterval(() => {
            const sec = Math.round((Date.now() - startedAt) / 1000);
            this.link.textContent = `別サーバーで動画生成中 ${statusText}... ${sec}s`;
        }, 1000);
        this.link.textContent = "別サーバーへ動画を受付中...";
        try {
            statusText = "受付中";
            const createResponse = await fetch(`${GCP_VIDEO_ENDPOINT}/jobs`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: requestBody,
                signal: this.serverAbortController.signal,
            });
            if (!createResponse.ok) {
                throw new Error(
                    await this.serverError(
                        createResponse,
                        "動画を受け付けられませんでした。",
                    ),
                );
            }
            const created = await createResponse.json();
            this.gcpJob = {
                id: String(created.id),
                accessToken: String(created.accessToken),
            };
            for (;;) {
                await this.abortableWait(
                    2000,
                    this.serverAbortController.signal,
                );
                const statusResponse = await fetch(
                    `${GCP_VIDEO_ENDPOINT}/jobs/${this.gcpJob.id}`,
                    {
                        headers: {
                            "X-Video-Token": this.gcpJob.accessToken,
                        },
                        signal: this.serverAbortController.signal,
                    },
                );
                if (!statusResponse.ok) {
                    throw new Error(
                        await this.serverError(
                            statusResponse,
                            "進捗を取得できません。",
                        ),
                    );
                }
                const status = await statusResponse.json();
                const stages: Record<string, string> = {
                    queued: "待機中",
                    launching: "起動中",
                    running: "描画中",
                    finalizing: "再エンコード中",
                };
                const frames =
                    status.totalFrames > 0
                        ? ` ${status.renderedFrames}/${status.totalFrames}コマ`
                        : "";
                statusText = `${stages[status.status] ?? status.status}${frames}`;
                if (status.status === "failed") {
                    throw new Error(status.error ?? "動画生成に失敗しました。");
                }
                if (status.status === "cancelled") {
                    throw new DOMException(
                        "動画作成を中止しました。",
                        "AbortError",
                    );
                }
                if (status.status !== "completed") {
                    continue;
                }
                const outputResponse = await fetch(
                    `${GCP_VIDEO_ENDPOINT}/jobs/${this.gcpJob.id}/output`,
                    {
                        headers: {
                            "X-Video-Token": this.gcpJob.accessToken,
                        },
                        signal: this.serverAbortController.signal,
                    },
                );
                if (!outputResponse.ok) {
                    throw new Error(
                        await this.serverError(
                            outputResponse,
                            "完成動画を取得できません。",
                        ),
                    );
                }
                const outputTicket = await outputResponse.json();
                const signedResponse = await fetch(String(outputTicket.url), {
                    signal: this.serverAbortController.signal,
                });
                if (!signedResponse.ok) {
                    throw new Error("完成動画の転送に失敗しました。");
                }
                const blob = await signedResponse.blob();
                const framesDone = status.output?.frames ?? status.totalFrames;
                this.finishDownload(
                    blob,
                    `動画完成(別サーバー) (${framesDone}コマ)`,
                );
                break;
            }
        } catch (error) {
            const message =
                error instanceof Error && error.name === "AbortError"
                    ? "中止しました。"
                    : error instanceof Error
                      ? error.message
                      : String(error);
            this.link.textContent = `別サーバー動画生成失敗: ${message}`;
            console.error(error);
        } finally {
            window.clearInterval(timer);
            this.gcpJob = null;
            this.serverAbortController = null;
            this.setUiBusy(false);
        }
    }
    private async serverError(response: Response, fallback: string) {
        try {
            return (await response.json()).error ?? fallback;
        } catch {
            return `${fallback} (HTTP ${response.status})`;
        }
    }
    private abortableWait(ms: number, signal: AbortSignal) {
        return new Promise<void>((resolve, reject) => {
            const abort = () => {
                window.clearTimeout(timer);
                reject(
                    new DOMException("動画作成を中止しました。", "AbortError"),
                );
            };
            const timer = window.setTimeout(() => {
                signal.removeEventListener("abort", abort);
                resolve();
            }, ms);
            signal.addEventListener("abort", abort, { once: true });
        });
    }
    // CF worker の進捗ストリームを読む: NDJSON 行 (progress/error) が続き、
    // {"type":"mp4","size":N,"meta":{...}} 行の直後に mp4 の生バイトが size 分続く
    private async readProgressStream(
        body: ReadableStream<Uint8Array>,
        onProgress: (done: number, total: number, stage?: string) => void,
    ): Promise<{
        blob: Blob;
        meta: { frames?: number; workerElapsedMs?: number };
    }> {
        const reader = body.getReader();
        let textBuffer = new Uint8Array(0); // 行パース中のバイト列 (バイナリ境界保持のためバイトのまま扱う)
        let mp4Info: { size: number; meta: Record<string, unknown> } | null =
            null;
        const chunks: Uint8Array[] = [];
        let received = 0;
        const handleLine = (line: string) => {
            if (!line.trim()) {
                return;
            }
            const event = JSON.parse(line);
            if (event.type === "progress") {
                onProgress(event.done ?? 0, event.total ?? 0, event.stage);
            } else if (event.type === "error") {
                throw new Error(String(event.error));
            } else if (event.type === "mp4") {
                mp4Info = { size: event.size, meta: event.meta ?? {} };
            }
        };
        for (;;) {
            const { done, value } = await reader.read();
            if (done) {
                break;
            }
            if (mp4Info !== null) {
                chunks.push(value);
                received += value.length;
                continue;
            }
            const merged = new Uint8Array(textBuffer.length + value.length);
            merged.set(textBuffer);
            merged.set(value, textBuffer.length);
            textBuffer = merged;
            let newline = textBuffer.indexOf(0x0a);
            while (newline >= 0 && mp4Info === null) {
                handleLine(
                    new TextDecoder().decode(textBuffer.subarray(0, newline)),
                );
                textBuffer = textBuffer.slice(newline + 1);
                newline = textBuffer.indexOf(0x0a);
            }
            if (mp4Info !== null && textBuffer.length > 0) {
                // mp4 行の直後から先はバイナリ
                chunks.push(textBuffer);
                received += textBuffer.length;
                textBuffer = new Uint8Array(0);
            }
        }
        if (mp4Info === null) {
            throw new Error("動画ストリームが完了しませんでした。");
        }
        const info: { size: number; meta: Record<string, unknown> } = mp4Info;
        if (received < info.size) {
            throw new Error(
                `動画データが途中で切れました (${received}/${info.size} bytes)。`,
            );
        }
        return {
            blob: new Blob(chunks, { type: "video/mp4" }),
            meta: info.meta,
        };
    }

    private async run(
        request: VideoRequest,
        signal: AbortSignal,
    ): Promise<VideoResult> {
        const started = performance.now();
        const range = this.host.getRange();
        // 既定の刻みは従来 toPNGs と同じ 0.1×FT間隔 (最小0.1h)。GS5 は 0.3h。
        const step =
            request.ftStep !== undefined
                ? new Decimal(request.ftStep)
                : Decimal.max(range.interval.mul(0.1), new Decimal(0.1));
        const start =
            request.ftStart !== undefined
                ? new Decimal(request.ftStart)
                : range.minimum;
        const end =
            request.ftEnd !== undefined
                ? new Decimal(request.ftEnd)
                : range.maximum;
        if (!step.isFinite() || step.lte(0)) {
            throw new Error("FT刻みが不正です。");
        }
        if (
            !start.isFinite() ||
            !end.isFinite() ||
            start.lt(range.minimum) ||
            end.gt(range.maximum) ||
            start.gt(end)
        ) {
            throw new Error(
                `FT範囲が不正です (${range.minimum}〜${range.maximum} の範囲で指定してください)。`,
            );
        }
        const total = end.sub(start).div(step).floor().toNumber() + 1;
        if (total > MAX_FRAMES) {
            throw new Error(
                `フレーム数が多すぎます (${total} > ${MAX_FRAMES})。FT範囲か刻みを見直してください。`,
            );
        }
        const frameFts = Array.from({ length: total }, (_, index) =>
            start.add(step.mul(index)),
        );
        await this.host.prepareFrames?.(frameFts);
        const fps = request.fps ?? 30;
        if (!Number.isFinite(fps) || fps < 1 || fps > 60) {
            throw new Error("fps は 1〜60 で指定してください。");
        }
        const [width, height] = this.resolveSize(request);

        // ---- エンコーダ設定 (typhoon_forecast の最終形を踏襲) ----
        // bitrate 明示 (bits/pixel/frame プリセット) or 「最高」は CQP。
        // 無指定だとブラウザ既定の不安定な低ビットレートになる。
        const quality = request.quality ?? "high";
        const bpp = BPP_TABLE[quality] ?? BPP_TABLE.high;
        const bitrate =
            request.bitrateKbps !== undefined && request.bitrateKbps !== null
                ? request.bitrateKbps * 1000
                : Math.round(width * height * fps * bpp);
        const baseConfig: VideoEncoderConfig = {
            codec: CODEC,
            width,
            height,
            displayWidth: width,
            displayHeight: height,
            framerate: fps,
            latencyMode: "quality",
            hardwareAcceleration: request.preferSoftware
                ? "prefer-software"
                : "no-preference",
        };
        const { supported } = await VideoEncoder.isConfigSupported(baseConfig);
        if (!supported) {
            throw new Error(
                `このブラウザは H.264 エンコード (${CODEC} ${width}x${height}) に対応していません。`,
            );
        }

        const timescale = 90000;
        const frameTime = Math.round(timescale / fps);
        const file = MP4Box.createFile();
        let videoTrack: number | null = null;
        let videoTime = 0;
        let keyFrames = 0;
        let encodeError: Error | null = null;
        const encoder = new VideoEncoder({
            output: (chunk, config) => {
                if (videoTrack === null) {
                    // duration を指定しないと Windows で duration 不明になる。
                    // media_duration は指定すると QuickTime の duration が壊れる。
                    videoTrack = file.addTrack({
                        timescale,
                        width,
                        height,
                        duration: (total - 1) * frameTime,
                        avcDecoderConfigRecord:
                            config?.decoderConfig?.description,
                    });
                }
                const buffer = new ArrayBuffer(chunk.byteLength);
                chunk.copyTo(buffer);
                file.addSample(videoTrack, buffer, {
                    duration: frameTime,
                    dts: videoTime,
                    cts: videoTime,
                    is_sync: chunk.type === "key",
                });
                videoTime += frameTime;
            },
            error: (error) => {
                encodeError =
                    error instanceof Error ? error : new Error(String(error));
            },
        });
        let bitrateMode: "quantizer" | "variable" = "variable";
        if (
            quality === "max" &&
            (request.bitrateKbps === undefined || request.bitrateKbps === null)
        ) {
            const config = {
                ...baseConfig,
                bitrateMode: "quantizer",
            } as VideoEncoderConfig;
            const q = await VideoEncoder.isConfigSupported(config);
            if (q.supported) {
                encoder.configure(config);
                bitrateMode = "quantizer";
            }
        }
        if (bitrateMode === "variable") {
            encoder.configure({
                ...baseConfig,
                bitrate,
                bitrateMode: "variable",
            });
        }

        const originalFt = this.host.getForecastTime();
        const maxGop = Math.round(fps * 4); // キーフレーム間隔の上限 4秒
        const minGop = fps; // 3hの切りのいいFTでも 1秒未満では打たない
        let lastKeyIndex = 0;
        this.downloader.beginVideoCapture();
        try {
            for (let i = 0; i < total; i++) {
                signal.throwIfAborted();
                request.signal?.throwIfAborted();
                if (encodeError) {
                    throw encodeError;
                }
                const ft = frameFts[i];
                await this.host.renderFrame(ft);
                const canvas = await this.downloader.toCanvas(width, height);
                const frame = new VideoFrame(canvas, {
                    timestamp: Math.round((i * 1_000_000) / fps),
                    duration: Math.round(1_000_000 / fps),
                });
                // キーフレーム = 3時間の切りのいいFT (シーク需要のある時刻)。
                // 頻度は 1秒に1回まで、間隔上限 4秒。
                const isKey =
                    i === 0 ||
                    (ft.mod(3).eq(0) && i - lastKeyIndex >= minGop) ||
                    i - lastKeyIndex >= maxGop;
                if (isKey) {
                    lastKeyIndex = i;
                    keyFrames++;
                }
                const options: VideoEncoderEncodeOptions & {
                    avc?: { quantizer: number };
                } = { keyFrame: isKey };
                if (bitrateMode === "quantizer") {
                    options.avc = { quantizer: QUANTIZER };
                }
                encoder.encode(frame, options);
                frame.close();
                while (encoder.encodeQueueSize > MAX_QUEUE) {
                    await new Promise((resolve) =>
                        encoder.addEventListener("dequeue", resolve, {
                            once: true,
                        }),
                    );
                }
                request.onProgress?.(i + 1, total);
            }
            await encoder.flush();
            if (encodeError) {
                throw encodeError;
            }
        } finally {
            this.downloader.endVideoCapture();
            if (encoder.state !== "closed") {
                encoder.close();
            }
            // 表示を動画開始前の FT へ戻す (失敗はコンソールのみ)
            await this.host
                .renderFrame(originalFt)
                .catch((error) => console.error(error));
        }
        if (request.audio !== false) {
            // 無音AACは既定で付与 (SNS等の音声トラック必須対策。audio:false 明示時のみ無し)
            addSilentAudioTrack(file, total / fps, timescale);
        }
        const blob = new Blob([file.getBuffer()], { type: "video/mp4" });
        return {
            blob,
            meta: {
                frames: total,
                keyFrames,
                width,
                height,
                fps,
                durationSec: total / fps,
                ftStart: start.toString(),
                ftEnd: start.add(step.mul(total - 1)).toString(),
                ftStep: step.toString(),
                codec: CODEC,
                bitrateMode,
                bitrate: bitrateMode === "variable" ? bitrate : null,
                elapsedMs: Math.round(performance.now() - started),
            },
        };
    }
    private resolveSize(request: VideoRequest): [number, number] {
        const [width, height] =
            request.width !== undefined && request.height !== undefined
                ? [request.width, request.height]
                : this.downloader.getSelectedSize();
        if (
            !Number.isInteger(width) ||
            !Number.isInteger(height) ||
            width <= 0 ||
            height <= 0 ||
            width % 2 !== 0 ||
            height % 2 !== 0
        ) {
            throw new Error("動画サイズは正の偶数で指定してください。");
        }
        return [width, height];
    }
    private revokeObjectUrl() {
        if (this.objectUrl) {
            URL.revokeObjectURL(this.objectUrl);
            this.objectUrl = null;
        }
    }
}

// ===== ブラウザ内 無音AAC付加 (typhoon_forecast から移植。エンコーダ非依存で Linux でも動く) =====
// AAC-LC 44.1kHz mono の無音1フレーム (1024サンプル分)。
// ffmpeg (anullsrc→aac) の定常フレーム実バイト (2026-07-12 抽出、フレーム間一定を確認済み)
const SILENT_AAC_FRAME = new Uint8Array([0x01, 0x18, 0x20, 0x07]);
const AAC_SAMPLE_RATE = 44100;
const AAC_SAMPLES_PER_FRAME = 1024;
// esds ボックスのペイロード。DecoderSpecificInfo = AudioSpecificConfig (AAC-LC/44100Hz/mono = 0x12,0x08)
const SILENT_ESDS_DATA = new Uint8Array([
    0x00, 0x00, 0x00, 0x00, // version + flags
    0x03, 0x19, 0x00, 0x00, 0x00, // ES_Descriptor (ES_ID=0)
    0x04, 0x11, 0x40, 0x15, // DecoderConfig: MPEG-4 Audio / AudioStream
    0x00, 0x00, 0x00, // bufferSizeDB
    0x00, 0x00, 0x1f, 0x40, // maxBitrate 8kbps
    0x00, 0x00, 0x1f, 0x40, // avgBitrate 8kbps
    0x05, 0x02, 0x12, 0x08, // DecoderSpecificInfo (ASC)
    0x06, 0x01, 0x02, // SLConfigDescriptor
]); // biome-ignore format:

function addSilentAudioTrack(
    file: MP4Box.MP4File,
    videoDurationSec: number,
    movieTimescale: number,
) {
    const nFrames = Math.max(
        1,
        Math.ceil((videoDurationSec * AAC_SAMPLE_RATE) / AAC_SAMPLES_PER_FRAME),
    );
    const trackId = file.addTrack({
        timescale: AAC_SAMPLE_RATE,
        // tkhd.duration は movie timescale、mdhd.duration は track timescale 単位。
        duration: Math.ceil(videoDurationSec * movieTimescale),
        media_duration: nFrames * AAC_SAMPLES_PER_FRAME,
        hdlr: "soun",
        name: "SoundHandler",
        type: "mp4a",
        samplerate: AAC_SAMPLE_RATE,
        channel_count: 1,
        samplesize: 16,
    });
    // mp4box.js は audio の esds 生成に未対応のため、mp4a サンプルエントリへ生ボックスとして追加する
    // (video の avcC と同じ addBox 経路。生 Box は .data がそのままシリアライズされる)
    const entry = file.getTrackById(trackId).mdia?.minf?.stbl?.stsd?.entries[0];
    if (!entry) {
        throw new Error(
            "無音AACトラックのサンプルエントリを取得できませんでした",
        );
    }
    const esds = new BoxParser.Box("esds");
    esds.data = SILENT_ESDS_DATA;
    entry.addBox(esds);
    for (let i = 0; i < nFrames; i++) {
        file.addSample(trackId, SILENT_AAC_FRAME.buffer as ArrayBuffer, {
            duration: AAC_SAMPLES_PER_FRAME,
            dts: i * AAC_SAMPLES_PER_FRAME,
            cts: i * AAC_SAMPLES_PER_FRAME,
            is_sync: true,
        });
    }
}
